import { memo } from "react"
import { useTranslation } from "react-i18next"

import { Floor } from "shared/types/Floor"
import { Icon } from "shared/ui/icon/Icon"
import { IconName } from "shared/ui/icon/__generated__/IconName"
import { TabBar } from "shared/ui/tab-bar/TabBar"

import {
  UNASSIGNED_PRODUCTS_TAB_ID,
  UNASSIGNED_ROOMS_TAB_ID
} from "./model/useMusterheftExportTabState"

export interface MusterheftExportTabBarProps {
  activeKey: string
  onSelect: (eventKey: string) => void
  floors: Floor[]
  unassignedRoomsCount: number
  unassignedProductsCount: number
}

export const MusterheftExportTabBar = memo(
  ({
    activeKey,
    onSelect,
    floors,
    unassignedRoomsCount,
    unassignedProductsCount
  }: MusterheftExportTabBarProps) => {
    const { t } = useTranslation()

    const hasUnassignedElements =
      unassignedProductsCount > 0 || unassignedRoomsCount > 0

    return (
      <TabBar.Root
        activeKey={activeKey}
        onSelect={onSelect}
        className="musterheft-export-tab-bar card card--elevated"
      >
        <div className="musterheft-export-tab-bar__icon">
          <Icon name={IconName.FLOOR} />
        </div>

        {hasUnassignedElements ? (
          <div className="musterheft-export-tab-bar__unassigned-tabs">
            {unassignedProductsCount > 0 ? (
              <TabBar.Tab
                index={-1}
                eventKey={UNASSIGNED_PRODUCTS_TAB_ID}
                label={t("Floor.UnassignedProducts")}
                aria-label={t("Floor.UnassignedProducts")}
                icon={IconName.PRODUCTS}
                count={unassignedProductsCount}
                isDragDisabled
              />
            ) : null}
            {unassignedRoomsCount > 0 ? (
              <TabBar.Tab
                index={-1}
                eventKey={UNASSIGNED_ROOMS_TAB_ID}
                label={t("Floor.UnassignedRooms")}
                aria-label={t("Floor.UnassignedRooms")}
                icon={IconName.ROOM}
                count={unassignedRoomsCount}
                isDragDisabled
              />
            ) : null}
          </div>
        ) : null}

        <TabBar.ScrollGroup
          scrollDistance={200}
          className="musterheft-export-tab-bar__floor-tabs"
        >
          <TabBar.ArrowLeft />
          <TabBar.ScrollContent>
            {floors.map((floor, index) => (
              <TabBar.Tab
                key={floor.id}
                index={index}
                eventKey={floor.id}
                label={floor.label ?? ""}
                aria-label={floor.label ?? undefined}
                icon={IconName.FLOOR}
                isDragDisabled
              />
            ))}
          </TabBar.ScrollContent>
          <TabBar.ArrowRight />
        </TabBar.ScrollGroup>
      </TabBar.Root>
    )
  }
)

MusterheftExportTabBar.displayName = "MusterheftExportTabBar"
